// src/pages/orderDelivery/utils.js

/**
 * Format a numeric value as a gross-weight string with three decimals and a "g" suffix.
 * Returns "0.000g" for null/undefined/empty/NaN inputs.
 *
 * @param {*} val - Raw value from orderList.grossWt / netWt (number | string | null)
 * @returns {string} e.g. "220.000g"
 */
export function formatGrossWt(val) {
  if (val === null || val === undefined || val === "") return "0.000g";
  const n = parseFloat(val);
  if (isNaN(n)) return "0.000g";
  return `${n.toFixed(3)}g`;
}

/**
 * Parse a gross weight value to a safe number for arithmetic.
 * Returns 0 for null/undefined/empty/NaN inputs.
 *
 * @param {*} val - Raw weight value (number | string | null)
 * @returns {number} parseFloat(val) or 0
 */
export function parseGrossWt(val) {
  if (val === null || val === undefined || val === "") return 0;
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
}

/**
 * Format a rupee amount as an Indian-currency string with exactly two decimals.
 * Example: 1234.5 → "₹1,234.50"
 *
 * @param {number} total - Amount in rupees
 * @returns {string} Formatted string prefixed with ₹
 */
export function formatRupee(total) {
  return `₹${total.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Compute the grand total cash from an array of seller groups.
 * @param {Array<{totalMc?: number}>} groups - Array of seller groups
 * @returns {number} Grand total rounded to 2 decimal places
 */
export function computeGrandTotalMc(groups) {
  return parseFloat(
    groups.reduce((sum, g) => sum + (g.totalMc || 0), 0).toFixed(2)
  );
}

/**
 * Safe number coercion — returns 0 for null/undefined/empty/NaN.
 * @param {*} val
 * @returns {number}
 */
export function num(val) {
  if (val === null || val === undefined || val === "") return 0;
  const parsed = parseFloat(val);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Format a date value to a localized short string representation.
 * @param {*} dateValue
 * @returns {string}
 */
export function formatDate(dateValue) {
  if (!dateValue) return "N/A";
  const date =
    typeof dateValue === "object" && dateValue.seconds
      ? new Date(dateValue.seconds * 1000)
      : new Date(dateValue);
  return isNaN(date.getTime())
    ? "N/A"
    : date.toLocaleDateString("en-US", { day: "numeric", month: "short" });
}

// ─── Static rates (swap for live fetch later) ────────────────────────────────
export const RATES = {
  RTGS_SALE_RATE: 12510,
  RTGS_PURCHASE_RATE: 12520,
  METAL_SALE_RATE: 12140,
  METAL_PURCHASE_RATE: 12150,
};

/**
 * Calculate the metal/cash split for a payment bucket.
 */
export function calcBucket({ metalDue, cashDue, mode, partialMetal, saleRate, purchaseRate }) {
  if (mode === "metal") {
    return {
      metalPayment: parseFloat((metalDue + cashDue / saleRate).toFixed(3)),
      cashPayment: 0,
    };
  }
  if (mode === "cash") {
    return {
      metalPayment: 0,
      cashPayment: parseFloat((cashDue + metalDue * purchaseRate).toFixed(2)),
    };
  }
  // Guard empty/non-numeric partialMetal for mixed mode
  let pm = 0;
  if (partialMetal) {
    const parsed = parseFloat(partialMetal);
    pm = isNaN(parsed) ? 0 : parsed;
  }

  if (metalDue >= pm) {
    return {
      metalPayment: pm,
      cashPayment: parseFloat((cashDue + (metalDue - pm) * purchaseRate).toFixed(2)),
    };
  }
  // partialMetal > metalDue → buyer delivers more metal than due, excess credited at sale rate
  return {
    metalPayment: pm,
    cashPayment: parseFloat((cashDue + (pm - metalDue) * saleRate).toFixed(2)),
  };
}
